// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Shield,
  Eye,
  ClipboardCheck,
  Target,
  FileText,
  LayoutGrid,
  Camera,
} from 'lucide-react';

import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { parseLocalDate } from '@/utils/formatters';
import ScoreSheetGenerator from '@/components/tournament/ScoreSheetGenerator';
import CourtScheduleDrawer from '@/components/tournament/CourtScheduleDrawer';
import AiScoreEntryDrawer from '@/components/staff/AiScoreEntryDrawer';


interface EventAssignment {
  event_id: string;
  permissions: string[];
  canScore?: boolean;
  canCheckIn?: boolean;
  staffCount: number;
  event: {
    id: string;
    name: string;
    slug?: string;
    start_date: string;
    end_date: string;
    venue_name?: string;
    status: string;
  };
}

const formatDateRange = (start: string, end?: string): string => {
  if (!start) return 'Date TBD';
  const s = parseLocalDate(start);
  const e = end ? parseLocalDate(end) : null;
  const monthDay = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!e || s.getTime() === e.getTime()) {
    return s.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${monthDay(s)}\u2013${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${monthDay(s)} \u2013 ${monthDay(e)}, ${e.getFullYear()}`;
};

const StaffDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [assignments, setAssignments] = useState<EventAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'upcoming' | 'past'>('upcoming');

  // Per-event drawer state
  const [scoreSheetEvent, setScoreSheetEvent] = useState<string | null>(null);
  const [courtSheetEvent, setCourtSheetEvent] = useState<string | null>(null);
  const [aiEntryEvent, setAiEntryEvent] = useState<string | null>(null);

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    fetchStaffAssignments();
  }, []);

  const fetchStaffAssignments = async () => {
    try {
      // rle-staff-home decides can_score / can_check_in per assignment.
      const { data: res, error } = await invokeEdgeFunction('rle-staff-home', { body: {} });
      if (error || res?.error) {
        console.error('rle-staff-home failed:', error || res?.error);
        setIsLoading(false);
        return;
      }
      const byEvent: Record<string, EventAssignment> = {};
      for (const a of res?.assignments || []) {
        if (!a?.event?.id) continue;
        const cur = byEvent[a.event.id];
        byEvent[a.event.id] = {
          event_id: a.event.id,
          permissions: [],
          canScore: !!a.can_score || !!cur?.canScore,
          canCheckIn: !!a.can_check_in || !!cur?.canCheckIn,
          staffCount: 1,
          event: a.event,
        };
      }
      const eventIds = Object.keys(byEvent);
      if (eventIds.length > 0) {
        const { data: countRows } = await supabase
          .from('event_staff_assignments')
          .select('event_id')
          .in('event_id', eventIds);
        const counts: Record<string, number> = {};
        (countRows || []).forEach((row: any) => {
          counts[row.event_id] = (counts[row.event_id] || 0) + 1;
        });
        eventIds.forEach((id) => { byEvent[id].staffCount = counts[id] || 1; });

        // rle-staff-home doesn't always include venue_name — fill from events.
        const missingVenue = eventIds.filter((id) => !byEvent[id].event?.venue_name);
        if (missingVenue.length > 0) {
          const { data: venueRows } = await supabase
            .from('events')
            .select('id, venue_name')
            .in('id', missingVenue);
          (venueRows || []).forEach((row: any) => {
            if (row.venue_name) byEvent[row.id].event.venue_name = row.venue_name;
          });
        }
      }
      setAssignments(Object.values(byEvent));
    } catch (error) {
      console.error('Error in fetchStaffAssignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permissions: string[], permission: string): boolean =>
    isAdmin || permissions.includes('all') || permissions.includes(permission);

  const filtered = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    return assignments.filter((a) => {
      const end = a.event?.end_date || a.event?.start_date;
      if (!end) return filter === 'upcoming';
      return filter === 'upcoming' ? end >= todayStr : end < todayStr;
    });
  }, [assignments, filter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading your assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">My Events</h1>
            <p className="text-sm text-muted-foreground">
              Events you're assigned to work
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm">
            {(['upcoming', 'past'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md font-medium capitalize transition ${
                  filter === f
                    ? 'bg-[#002E62] text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {assignments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-1">No event assignments yet</h3>
            <p className="text-muted-foreground">
              Check with your tournament director.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-muted-foreground">
            No {filter} events.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {filtered.map((a) => (
              <div
                key={a.event_id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3"
              >
                {/* LEFT */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{a.event.name}</span>
                    <Badge
                      className={
                        a.event.status === 'published'
                          ? 'bg-green-100 text-green-700 hover:bg-green-100 border-transparent'
                          : 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent'
                      }
                    >
                      {a.event.status === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {formatDateRange(a.event.start_date, a.event.end_date)}
                    {a.event.venue_name ? ` \u00B7 ${a.event.venue_name}` : ''}
                    {` \u00B7 ${a.staffCount} staff`}
                  </p>
                </div>

                {/* RIGHT */}
                <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                  {a.canScore && (
                    <Button
                      className="h-11 px-4 bg-primary text-primary-foreground"
                      onClick={() => navigate(`/staff/scoring/${a.event_id}`)}
                    >
                      <Target className="h-4 w-4 mr-1.5" /> Score games
                    </Button>
                  )}
                  {a.canCheckIn && (
                    <Button
                      variant="outline"
                      className="h-11 px-4"
                      onClick={() => navigate(`/staff/checkin/${a.event_id}`)}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-1.5" /> Check in teams
                    </Button>
                  )}
                  {(isAdmin || a.canCheckIn) && (
                    <IconAction
                      label="Check-In"
                      icon={ClipboardCheck}
                      onClick={() =>
                        window.open(`/staff/checkin-sheet?event=${a.event_id}`, '_blank')
                      }
                    />
                  )}

                  <IconAction
                    label="Score Sheets"
                    icon={FileText}
                    onClick={() => setScoreSheetEvent(a.event_id)}
                  />
                  <IconAction
                    label="Court Sheets"
                    icon={LayoutGrid}
                    onClick={() => setCourtSheetEvent(a.event_id)}
                  />
                  {(isAdmin || a.canScore) && (
                    <IconAction
                      label="AI Score Entry"
                      icon={Camera}
                      onClick={() => setAiEntryEvent(a.event_id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-event mounts */}
      {scoreSheetEvent && (
        <ScoreSheetGenerator
          eventId={scoreSheetEvent}
          open={!!scoreSheetEvent}
          onClose={() => setScoreSheetEvent(null)}
        />
      )}
      {courtSheetEvent && (
        <CourtScheduleDrawer
          eventId={courtSheetEvent}
          open={!!courtSheetEvent}
          onClose={() => setCourtSheetEvent(null)}
        />
      )}

      {/* AI Score Entry */}
      {aiEntryEvent && (
        <AiScoreEntryDrawer
          eventId={aiEntryEvent}
          open={!!aiEntryEvent}
          onClose={() => setAiEntryEvent(null)}
        />
      )}

    </TooltipProvider>
  );
};

const IconAction: React.FC<{
  label: string;
  icon: React.ElementType;
  onClick: () => void;
}> = ({ label, icon: Icon, onClick }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="outline"
        size="icon"
        className="h-[34px] w-[34px]"
        aria-label={label}
        onClick={onClick}
      >
        <Icon className="h-4 w-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export default StaffDashboard;
